/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import { labels } from '@umati/dashboard-common';
import { IMachine, IMachineData } from '@umati/dashboard-types';
import React, { ReactElement } from 'react';
import { TemplateContext } from 'templates/pubSub/types/TemplateContextType';
import ENVs from 'utilities/ENVs';
import MachineHandling from 'utilities/MachineHandling';
import { useNamespaceIndex, usePubSubNode, usePubSubValue } from 'utilities/PubSub/PubSubHooks';
import TemplateHelperPubSub from 'utilities/TemplateHelperPubSub';
import SupportedBy from './SupportedBy';
import { useSelector } from 'react-redux';
import { StoreType } from 'redux/store';
import DetailGalleryPubSub from 'components/detail/common/DetailGalleryPubSub';
import StatusTimeline from 'components/timeline/StatusTimeline';
import DetailDivs from 'views/DetailDivs';
import SectionCards from 'components/detail/template/pubSub/SectionCards';
import HardCodedDetailDiv from './HardCodedDetailDiv';
import '../../styles/tvViewDetail.scss';
import { ITvViewParams } from './tvView';

interface ITvViewDetailPubSubProps {
  machine: IMachine;
  params: ITvViewParams;
}

export default function TvViewDetailPubSub(props: ITvViewDetailPubSubProps): ReactElement {
  const machine = props.machine;
  const template = TemplateHelperPubSub.getTemplateByTypeId(machine.TypeDefinition);

  const ns = useNamespaceIndex(template.namespaceUris);
  const templateContext: TemplateContext = { namespaces: ns, template: template };

  const filterInstanceUri = TemplateHelperPubSub.createFilter(ns, template.header.nameVariable?.variablePath);
  const nodeInstanceUri = usePubSubNode(filterInstanceUri);
  const valueInstanceUri = usePubSubValue(nodeInstanceUri);

  const filterModel = TemplateHelperPubSub.createFilter(ns, template.header.modelVariable?.variablePath);
  const nodeModel = usePubSubNode(filterModel);
  const valueModel = usePubSubValue(nodeModel);

  const filterDefaultImagePathClassifier = TemplateHelperPubSub.createFilter(
    ns,
    template.imagesConfig.defaultImagePathClassifierVariable?.variablePath,
  );
  const nodeDefaultImagePathClassifier = usePubSubNode(filterDefaultImagePathClassifier);
  const valueDefaultImagePathClassifier = usePubSubValue(nodeDefaultImagePathClassifier);

  const watermarkedBucketReachable = useSelector(
    (state: StoreType) => state.buckets?.watermarkedBucketReachable || false,
  );

  const instanceUri = template.header.nameShowStaticText
    ? template.header.nameStaticText || 'undefined'
    : !valueInstanceUri
      ? MachineHandling.getProductInstanceUri(machine) || 'undefined'
      : valueInstanceUri;
  const model = template.header.modelShowStaticText
    ? template.header.modelStaticText || 'undefined'
    : !valueModel
      ? MachineHandling.getMachineModel(machine) || 'undefined'
      : valueModel;

  const getMachineHall = () => {
    return machine?.Positions?.[props.params.fair]?.['hall'];
  };

  const getMachineBooth = () => {
    return machine?.Positions?.[props.params.fair]?.['booth'];
  };

  const hall = getMachineHall();
  const booth = getMachineBooth();
  const hasHallAndBooth = hall && booth;
  return (
    <div className="view-tvview-detail">
      <div className="container-hero-bg"></div>
      <div className="container-hero hero row">
        <div className="col">
          <div className="hero-title">
            <strong>{instanceUri}</strong>
            <h2 className="display-3">{model}</h2>
          </div>
        </div>
        <div className="hall-booth col-auto">{hasHallAndBooth && DetailDivs.getHallAndBoothDiv(hall, booth)}</div>
      </div>
      <div className="container-gal">
        <DetailGalleryPubSub
          templateContext={templateContext}
          imagePath={machine?.ImagePath!}
          machineId={machine.MachineId}
          defaultImagePathClassifier={valueDefaultImagePathClassifier}
          machineType={machine.TypeDefinition}
        />
      </div>
      <div className="container-status mb-1 mb-md-2 mb-lg-4">
        <h3 className="h5">{labels.statusOverview}</h3>
        <div className="status-timeline">
          <StatusTimeline
            namespaceUri={machine.MachineId}
            colorMap={TemplateHelperPubSub.getTimelineColorMap(template.timeline)}
          />
        </div>
      </div>
      <div className="container-content auto-columns-2">
        {machine && !machine.Online ? (
          <HardCodedDetailDiv data={machine.Data} />
        ) : (
          <SectionCards templateContext={templateContext} />
        )}
      </div>
      <div className="container-footer">
        <div className="row justify-content-around" style={{ paddingTop: '1rem' }}>
          <SupportedBy
            key="isw"
            logoSrc={`${ENVs.WatermarkedBucketUrl(watermarkedBucketReachable)}/watermarked/TvView/logo_isw.png`}
            altTxt="Logo ISW"
          />
          <SupportedBy
            key="interop4x"
            logoSrc={`${ENVs.WatermarkedBucketUrl(watermarkedBucketReachable)}/watermarked/TvView/logo_interop4x.png`}
            altTxt="Logo interop4X"
          />
        </div>
      </div>
    </div>
  );
}
